'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import AppLayout from '@/components/AppLayout';
import { Search, BookMarked, ChevronRight, Bookmark, User } from 'lucide-react';
import CollectionsSidebar, { type Collection } from './components/CollectionsSidebar';
import SaveToCollectionModal from './components/SaveToCollectionModal';
import SearchOverlay from './components/SearchOverlay';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { EditorialCard } from './components/editorialData';

// ─── Hero Data ───────────────────────────────────────────────────────────────
const heroSlides = [
{
  id: 'hero-1',
  label: 'FEATURED DISCOVERY',
  title: '15 Extraordinary Hotels in Italy',
  subtitle: 'Most Tourists Will Never Discover',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ece8dfaa-1772339012769.png",
  alt: 'Stunning Italian coastal villa with terrace overlooking turquoise Mediterranean sea with umbrella pine trees',
  cta: 'EXPLORE NOW',
  href: '/article?slug=italy-hotels'
},
{
  id: 'hero-2',
  label: 'FEATURED DISCOVERY',
  title: 'Private Islands You Can Actually Stay On',
  subtitle: 'Beyond the Postcards',
  image: "https://images.unsplash.com/photo-1683650904081-78afc08950cb",
  alt: 'Secluded tropical private island with crystal clear turquoise lagoon and overwater bungalows',
  cta: 'EXPLORE NOW',
  href: '/article?slug=private-islands'
},
{
  id: 'hero-3',
  label: 'FEATURED DISCOVERY',
  title: "The World\'s Most Secret Wine Estates",
  subtitle: 'Beyond the Labels',
  image: "https://images.unsplash.com/photo-1631128361664-38637dd6593d",
  alt: 'Rolling vineyard hills at golden hour with ancient stone chateau in the background',
  cta: 'EXPLORE NOW',
  href: '/article?slug=secret-wine-estates'
},
{
  id: 'hero-4',
  label: 'FEATURED DISCOVERY',
  title: 'Hidden Experiences in Switzerland',
  subtitle: 'Beyond the Postcards',
  image: "https://images.unsplash.com/photo-1692912363859-28d7a18ff1d6",
  alt: 'Dramatic Swiss alpine landscape with snow-capped peaks reflecting in pristine mountain lake',
  cta: 'EXPLORE NOW',
  href: '/article?slug=switzerland-hidden'
},
{
  id: 'hero-5',
  label: 'FEATURED DISCOVERY',
  title: 'Whisky Collections So Rare Even Collectors May Never See Them',
  subtitle: 'Hidden Treasures of Horology',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c387bbb3-1778342652064.png",
  alt: 'Aged whisky bottles and crystal glasses arranged on dark oak table in candlelit private cellar',
  cta: 'EXPLORE NOW',
  href: '/article?slug=whisky-collections'
}];


// ─── Worlds Data ──────────────────────────────────────────────────────────────
const worlds = [
{
  id: 'w-1',
  label: 'Extraordinary Places',
  sub: 'Hidden hotels, villas & secret estates',
  href: '/category?cat=extraordinary-places',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10c07900b-1766424943661.png",
  alt: 'Aerial view of luxury coastal resort with infinity pool overlooking turquoise sea'
},
{
  id: 'w-2',
  label: 'Luxury Travel',
  sub: 'Unusual journeys & unique experiences',
  href: '/category?cat=luxury-travel',
  image: "https://images.unsplash.com/photo-1722449302757-558e6d5e7f5e",
  alt: 'Luxury train winding through dramatic mountain scenery'
},
{
  id: 'w-3',
  label: 'Art & Collecting',
  sub: 'Rare art, auctions & private collections',
  href: '/category?cat=art-collecting',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_155524d9e-1767590662915.png",
  alt: 'Grand art gallery with Renaissance paintings in gilded frames under dramatic lighting'
},
{
  id: 'w-4',
  label: 'Whisky & Spirits',
  sub: 'Rare bottles, distilleries & collections',
  href: '/category?cat=whisky-spirits',
  image: "https://images.unsplash.com/photo-1716719215097-a6a640fc3225",
  alt: 'Rare whisky bottles lined up in dimly lit private cellar with amber liquid glowing'
},
{
  id: 'w-5',
  label: 'Watches & Horology',
  sub: 'Iconic timepieces & rare finds',
  href: '/category?cat=watches-horology',
  image: "https://images.unsplash.com/photo-1473640178573-11bc2032a78a",
  alt: 'Exquisite luxury watch with intricate dial and gold case on dark velvet display'
},
{
  id: 'w-6',
  label: 'Private Clubs',
  sub: 'Exclusive clubs & secret societies',
  href: '/category?cat=private-clubs',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15fcf0931-1774672633931.png",
  alt: 'Opulent private members club interior with leather armchairs and dark wood panelling'
}];


// ─── Today's Discoveries ──────────────────────────────────────────────────────
const todaysDiscoveries = [
{
  id: 'td-1',
  title: "The World\'s Strangest Private Clubs",
  sub: 'And what it really takes to enter.',
  href: '/article?slug=strange-private-clubs',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_105890ab8-1786801666215.png",
  alt: 'Mysterious private club entrance with ornate door and dim lighting'
},
{
  id: 'td-2',
  title: 'The Watch Collections That Time Almost Forgot',
  sub: 'Hidden treasures of horology.',
  href: '/article?slug=watch-collections',
  image: "https://images.unsplash.com/photo-1712325409515-85133a386aa2",
  alt: 'Rare vintage watch collection displayed in velvet-lined case'
},
{
  id: 'td-3',
  title: 'Hidden Experiences in Switzerland',
  sub: 'Beyond the postcards.',
  href: '/article?slug=switzerland-hidden',
  image: "https://images.unsplash.com/photo-1650892549538-45d979baabec",
  alt: 'Secret alpine valley in Switzerland with pristine snow and dramatic peaks'
},
{
  id: 'td-4',
  title: 'Whisky Collections So Rare Even Collectors May Never See Them',
  sub: 'Hidden treasures of horology.',
  href: '/article?slug=whisky-collections',
  image: "https://images.unsplash.com/photo-1541516000549-6bd1bb395fbb",
  alt: 'Ultra-rare whisky bottles in private collection with aged labels'
},
{
  id: 'td-5',
  title: 'Private Islands You Can Actually Stay On',
  sub: 'And how to get there.',
  href: '/article?slug=private-islands',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f06bc66a-1772211486737.png",
  alt: 'Exclusive private island resort with overwater villas and turquoise lagoon'
}];


// ─── Hidden Gems ──────────────────────────────────────────────────────────────
const hiddenGems = [
{
  id: 'hg-1',
  title: 'Undiscovered Beach Resorts in Greece',
  sub: 'Places only a few know.',
  href: '/article?slug=switzerland-hidden',
  image: "https://images.unsplash.com/photo-1609185348031-cd85a02212bd",
  alt: 'Secluded Greek beach cove with crystal clear water and white limestone cliffs'
},
{
  id: 'hg-2',
  title: 'Secret Villas in Bali That Feel Like Another World',
  sub: 'Beyond the usual.',
  href: '/article?slug=italy-hotels',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a3ae0155-1772713570225.png",
  alt: 'Hidden Balinese villa with infinity pool surrounded by lush tropical jungle'
},
{
  id: 'hg-3',
  title: 'Private Dining Rooms Worth Travelling For',
  sub: 'Where chefs create magic.',
  href: '/article?slug=strange-private-clubs',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c6f0a31c-1772055026304.png",
  alt: 'Intimate private dining room with candlelit table and sommelier serving rare wine'
}];


// ─── Expert Picks ─────────────────────────────────────────────────────────────
const expertPicks = [
{
  id: 'ep-1',
  title: "The Most Beautiful Cars You've Never Heard Of",
  sub: 'Engineering meets art.',
  href: '/article?slug=beautiful-cars',
  image: "https://images.unsplash.com/photo-1572217453948-850c56ecd51b",
  alt: 'Rare exotic supercar in matte black finish on empty mountain road at dusk'
},
{
  id: 'ep-2',
  title: 'Superyachts With Stories More Incredible Than Their Price Tags',
  sub: 'Not just luxury, legacy.',
  href: '/article?slug=superyachts',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a6909e3d-1772330963294.png",
  alt: 'Magnificent superyacht anchored in Mediterranean bay with tender boats alongside'
},
{
  id: 'ep-3',
  title: 'A Legendary Watch House Unveils Its Rarest Creation Yet',
  sub: 'Limited. Exceptional. Unseen.',
  href: '/article?slug=watch-collections',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d62ed3b3-1786801665105.png",
  alt: 'Master watchmaker presenting ultra-rare limited edition timepiece at press event'
}];


// ─── Who Owns It ──────────────────────────────────────────────────────────────
const whoOwnsIt = [
{
  id: 'wo-1',
  title: "Who Owns Europe\'s Most Iconic Grand Hotels?",
  sub: 'The names behind the legends.',
  href: '/article?slug=grand-hotels-ownership',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_155aa086d-1772619279995.png",
  alt: 'Grand European hotel facade with ornate architecture and doorman in uniform'
},
{
  id: 'wo-2',
  title: 'Who Owns These Private Islands?',
  sub: 'The surprising stories behind paradise.',
  href: '/article?slug=private-islands',
  image: "https://images.unsplash.com/photo-1564458121764-515eeec33fb5",
  alt: 'Aerial view of exclusive private island with lush vegetation and private dock'
},
{
  id: 'wo-3',
  title: "Who Owns the World\'s Most Famous Wine Estates?",
  sub: 'Beyond the labels.',
  href: '/article?slug=secret-wine-estates',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_16555ee83-1773187409608.png",
  alt: 'Prestigious wine estate with ancient stone chateau surrounded by manicured vineyards'
}];


// ─── Today in Luxury ──────────────────────────────────────────────────────────
const todayInLuxury = [
{
  id: 'til-1',
  title: "New Private Members' Club Opens in London Hotel",
  date: '18 Jul 2026',
  href: '/article?slug=london-members-club',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5880a4e-1781492798743.png",
  alt: 'Exclusive new members club interior with contemporary luxury design in London'
},
{
  id: 'til-2',
  title: 'A Historic Palace in Rajasthan Is Now a Luxury Hotel',
  date: '15 Jul 2026',
  href: '/article?slug=rajasthan-palace',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cf42b224-1766854513420.png",
  alt: 'Magnificent Rajasthani palace with ornate architecture converted to luxury heritage hotel'
},
{
  id: 'til-3',
  title: 'Rare Patek Philippe Sells for Record Price at Auction',
  date: '12 Jul 2026',
  href: '/article?slug=patek-auction',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bac840d2-1772903969337.png",
  alt: 'Rare Patek Philippe watch on display at prestigious auction house'
}];


export default function EditorialDiscoveryFeedPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [saveModal, setSaveModal] = useState<EditorialCard | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const { user } = useAuth();
  const supabase = createClient();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides?.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Load collections from Supabase
  useEffect(() => {
    if (!user) return;
    const fetchCollections = async () => {
      try {
        const { data } = await supabase.
        from('collections').
        select('id, name, emoji, item_count').
        eq('user_id', user.id).
        order('created_at', { ascending: true });
        if (data) setCollections(data);
      } catch {}
    };
    fetchCollections();
  }, [user]);

  const slide = heroSlides?.[currentSlide];

  return (
    <AppLayout currentPath="/editorial-discovery-feed">
      <div className="bg-white min-h-screen">
        {/* ── Top Bar ── */}
        <div className="hidden lg:flex items-center justify-between px-6 py-3 border-b border-[var(--border)] bg-white sticky top-0 z-30">
          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-2 flex-1 max-w-md border border-[var(--border)] rounded px-3 py-2 bg-white hover:border-[var(--gold)] transition-colors cursor-pointer text-left">
            
            <Search size={15} className="text-[var(--slate-muted)]" />
            <span className="flex-1 text-sm text-[var(--slate-muted)]">Search for places, collections, experiences...</span>
            <span className="text-[10px] border border-[var(--border)] px-1.5 py-0.5 rounded text-[var(--slate-muted)]">⌘K</span>
          </button>
          <div className="flex items-center gap-3 ml-4">
            <button
              onClick={() => setCollectionsOpen(true)}
              className="flex items-center gap-2 px-4 py-2 border border-[var(--border)] rounded text-sm font-medium text-[var(--foreground)] hover:border-[var(--gold)] transition-colors">
              <BookMarked size={15} className="text-[var(--gold)]" />
              MY COLLECTIONS
            </button>
            <div className="w-8 h-8 rounded-full bg-[var(--muted)] overflow-hidden flex items-center justify-center">
              <User size={16} className="text-[var(--slate-muted)]" />
            </div>
          </div>
        </div>

        {/* Mobile search bar */}
        <div className="lg:hidden px-4 py-3 border-b border-[var(--border)] bg-white sticky top-16 z-30">
          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-2 w-full border border-[var(--border)] rounded px-3 py-2 bg-white hover:border-[var(--gold)] transition-colors">
            
            <Search size={15} className="text-[var(--slate-muted)]" />
            <span className="flex-1 text-sm text-[var(--slate-muted)] text-left">Search discoveries...</span>
          </button>
        </div>

        {/* ── Main Content ── */}
        <div className="flex gap-0 w-full min-w-0 overflow-x-hidden">
          {/* ── Left + Center ── */}
          <div className="flex-1 min-w-0 w-full overflow-x-hidden">

            {/* ── Hero Carousel ── */}
            <div className="relative h-[340px] lg:h-[380px] overflow-hidden">
              <AppImage
                src={slide?.image}
                alt={slide?.alt}
                fill
                priority
                className="object-cover transition-opacity duration-700" />
              
              <div className="absolute inset-0 hero-overlay-bottom" />
              <div className="absolute inset-0 hero-overlay" />
              <div className="absolute inset-0 flex flex-col justify-end p-6 lg:p-8">
                <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[var(--gold)] mb-2">{slide?.label}</p>
                <h1 className="font-editorial text-2xl lg:text-3xl xl:text-4xl font-bold text-white leading-tight max-w-lg mb-1">
                  {slide?.title}
                </h1>
                <p className="text-white/80 text-sm mb-4">{slide?.subtitle}</p>
                <Link href={slide?.href} className="self-start px-5 py-2 border border-[var(--gold)] text-[var(--gold)] text-xs font-bold tracking-[0.15em] uppercase rounded hover:bg-[var(--gold)] hover:text-white transition-all">
                  {slide?.cta}
                </Link>
              </div>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {heroSlides?.map((_, i) =>
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`h-2 rounded-full transition-all ${i === currentSlide ? 'bg-[var(--gold)] w-4' : 'bg-white/50 w-2'}`}
                  aria-label={`Slide ${i + 1}`} />

                )}
              </div>
            </div>

            {/* ── Explore the Worlds ── */}
            <div className="px-4 lg:px-6 py-5 border-b border-[var(--border)]">
              <div className="flex items-center justify-between mb-3">
                <h2 className="section-title">EXPLORE THE WORLDS</h2>
                <Link href="/category" className="view-all-link">View All Worlds</Link>
              </div>
              <div className="relative">
                <div className="flex gap-2 overflow-x-auto scroll-hidden pb-1">
                  {worlds?.map((w) =>
                  <Link key={w?.id} href={w?.href} className="flex-shrink-0 w-[140px] lg:w-[155px] relative rounded overflow-hidden cursor-pointer group editorial-card-hover block">
                      <div className="relative h-[100px]">
                        <AppImage src={w?.image} alt={w?.alt} fill className="object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-colors" />
                      </div>
                      <div className="absolute inset-0 flex flex-col justify-end p-2">
                        <p className="text-white text-[10px] font-bold tracking-[0.08em] uppercase leading-tight">{w?.label}</p>
                        <p className="text-white/70 text-[9px] mt-0.5 leading-tight">{w?.sub}</p>
                      </div>
                    </Link>
                  )}
                </div>
                <button className="absolute right-0 top-1/2 -translate-y-1/2 w-7 h-7 bg-white border border-[var(--border)] rounded-full flex items-center justify-center shadow-sm">
                  <ChevronRight size={14} className="text-[var(--foreground)]" />
                </button>
              </div>
            </div>

            {/* ── Hidden Gems + Expert Picks side by side ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-b border-[var(--border)]">
              {/* Hidden Gems */}
              <div className="px-4 lg:px-6 py-5 border-b lg:border-b-0 lg:border-r border-[var(--border)]">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="section-title">HIDDEN GEMS</h2>
                  <Link href="/category?cat=hidden-gems" className="view-all-link">View All</Link>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {hiddenGems?.map((gem) =>
                  <Link key={gem?.id} href={gem?.href} className="relative rounded overflow-hidden cursor-pointer group editorial-card-hover block">
                      <div className="relative h-[110px] lg:h-[130px]">
                        <AppImage src={gem?.image} alt={gem?.alt} fill className="object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute top-2 right-2 w-6 h-6 bg-white/90 rounded-full flex items-center justify-center">
                          <Bookmark size={11} className="text-[var(--foreground)]" />
                        </div>
                      </div>
                      <div className="pt-1.5 pb-1">
                        <p className="text-[11px] font-semibold text-[var(--foreground)] leading-tight line-clamp-2 group-hover:text-[var(--gold)] transition-colors">{gem?.title}</p>
                        <p className="text-[10px] text-[var(--slate-muted)] mt-0.5">{gem?.sub}</p>
                      </div>
                    </Link>
                  )}
                </div>
              </div>

              {/* Expert Picks */}
              <div className="px-4 lg:px-6 py-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="section-title">EXPERT PICKS</h2>
                  <Link href="/category?cat=expert-picks" className="view-all-link">View All</Link>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {expertPicks?.map((pick) =>
                  <Link key={pick?.id} href={pick?.href} className="relative rounded overflow-hidden cursor-pointer group editorial-card-hover block">
                      <div className="relative h-[110px] lg:h-[130px]">
                        <AppImage src={pick?.image} alt={pick?.alt} fill className="object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute top-2 right-2 w-6 h-6 bg-white/90 rounded-full flex items-center justify-center">
                          <Bookmark size={11} className="text-[var(--foreground)]" />
                        </div>
                      </div>
                      <div className="pt-1.5 pb-1">
                        <p className="text-[11px] font-semibold text-[var(--foreground)] leading-tight line-clamp-2 group-hover:text-[var(--gold)] transition-colors">{pick?.title}</p>
                        <p className="text-[10px] text-[var(--slate-muted)] mt-0.5">{pick?.sub}</p>
                      </div>
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* ── Who Owns It + Today in Luxury ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-b border-[var(--border)]">
              {/* Who Owns It */}
              <div className="px-4 lg:px-6 py-5 border-b lg:border-b-0 lg:border-r border-[var(--border)]">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="section-title">WHO OWNS IT?</h2>
                  <Link href="/category?cat=who-owns-it" className="view-all-link">View All</Link>
                </div>
                <div className="flex flex-col gap-3">
                  {whoOwnsIt?.map((item) =>
                  <Link key={item?.id} href={item?.href} className="flex gap-3 cursor-pointer group">
                      <div className="relative w-[80px] h-[56px] flex-shrink-0 rounded overflow-hidden">
                        <AppImage src={item?.image} alt={item?.alt} fill className="object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-semibold text-[var(--foreground)] leading-tight line-clamp-2 group-hover:text-[var(--gold)] transition-colors">{item?.title}</p>
                        <p className="text-[11px] text-[var(--slate-muted)] mt-0.5">{item?.sub}</p>
                      </div>
                      <ChevronRight size={14} className="text-[var(--slate-muted)] flex-shrink-0 mt-1" />
                    </Link>
                  )}
                </div>
              </div>

              {/* Today in Luxury */}
              <div className="px-4 lg:px-6 py-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="section-title">TODAY IN LUXURY</h2>
                  <Link href="/intelligence-desk" className="view-all-link">View All</Link>
                </div>
                <div className="flex flex-col gap-3">
                  {todayInLuxury?.map((item) =>
                  <Link key={item?.id} href={item?.href} className="flex gap-3 cursor-pointer group">
                      <div className="relative w-[80px] h-[56px] flex-shrink-0 rounded overflow-hidden">
                        <AppImage src={item?.image} alt={item?.alt} fill className="object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-semibold text-[var(--foreground)] leading-tight line-clamp-2 group-hover:text-[var(--gold)] transition-colors">{item?.title}</p>
                        <p className="text-[10px] text-[var(--slate-muted)] mt-1">{item?.date}</p>
                      </div>
                      <ChevronRight size={14} className="text-[var(--slate-muted)] flex-shrink-0 mt-1" />
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* ── The Private Edition Banner ── */}
            <div className="mx-4 lg:mx-6 my-6 rounded overflow-hidden relative">
              <div className="relative h-[160px]">
                <AppImage
                  src="https://images.unsplash.com/photo-1646568780034-52b944cd01ae"
                  alt="Elegant wax-sealed envelope on cream linen representing The Private Edition newsletter"
                  fill
                  className="object-cover" />
                
                <div className="absolute inset-0 bg-black/60" />
              </div>
              <div className="absolute inset-0 flex items-center px-6 lg:px-10">
                <div className="flex-1">
                  <h3 className="font-editorial text-xl lg:text-2xl font-bold text-white">THE PRIVATE EDITION</h3>
                  <p className="text-white/80 text-sm mt-1 max-w-xs">A weekly curation of the world's most extraordinary and exclusive discoveries.</p>
                  <Link href="/category?cat=private-edition" className="inline-block mt-3 px-5 py-2 border border-white text-white text-xs font-bold tracking-[0.12em] uppercase rounded hover:bg-white hover:text-[var(--foreground)] transition-all">
                    READ LATEST EDITION
                  </Link>
                </div>
                <div className="hidden lg:grid grid-cols-4 gap-6 ml-8">
                  {[
                  { icon: '♛', label: 'CURATED', sub: 'Not crowded.\nOnly the exceptional.' },
                  { icon: '◎', label: 'TRUSTED', sub: 'Researched.\nVerified.\nDiscerning.' },
                  { icon: '🔒', label: 'PRIVATE', sub: 'For members.\nNot for everyone.' },
                  { icon: '⧗', label: 'TIMELESS', sub: 'Beyond trends.\nBeyond ordinary.' }]?.
                  map((item) =>
                  <div key={item?.label} className="text-center">
                      <p className="text-white text-lg mb-1">{item?.icon}</p>
                      <p className="text-white text-[10px] font-bold tracking-[0.12em]">{item?.label}</p>
                      <p className="text-white/60 text-[9px] mt-1 whitespace-pre-line leading-tight">{item?.sub}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Right Sidebar: Today's Private Discoveries ── */}
          <aside className="hidden xl:block w-[280px] flex-shrink-0 border-l border-[var(--border)] bg-white">
            <div className="sticky top-[57px] p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="section-title">TODAY'S PRIVATE DISCOVERIES</h2>
                <Link href="/category?cat=discoveries" className="view-all-link">View All</Link>
              </div>

              <div className="flex flex-col gap-0">
                {todaysDiscoveries?.map((item) =>
                <Link key={item?.id} href={item?.href} className="flex gap-3 py-3 border-b border-[var(--border)] last:border-0 cursor-pointer group">
                    <div className="relative w-[72px] h-[52px] flex-shrink-0 rounded overflow-hidden">
                      <AppImage src={item?.image} alt={item?.alt} fill className="object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-[var(--foreground)] leading-tight line-clamp-2 group-hover:text-[var(--gold)] transition-colors">{item?.title}</p>
                      <p className="text-[11px] text-[var(--slate-muted)] mt-0.5">{item?.sub}</p>
                    </div>
                    <ChevronRight size={13} className="text-[var(--slate-muted)] flex-shrink-0 mt-1" />
                  </Link>
                )}
              </div>

              <Link href="/category?cat=discoveries" className="block w-full mt-3 py-2.5 border border-[var(--border)] text-[11px] font-bold tracking-[0.12em] uppercase text-center text-[var(--foreground)] rounded hover:border-[var(--gold)] hover:text-[var(--gold)] transition-all">
                VIEW ALL DISCOVERIES
              </Link>
            </div>
          </aside>
        </div>
      </div>

      {/* Search Overlay */}
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Collections Sidebar */}
      <CollectionsSidebar
        open={collectionsOpen}
        onClose={() => setCollectionsOpen(false)}
        collections={collections}
        onCollectionsChange={setCollections} />
      

      {/* Save to Collection Modal */}
      {saveModal &&
      <SaveToCollectionModal
        card={saveModal}
        collections={collections}
        onSave={(colId) => {
          setCollections((prev) =>
          prev.map((c) => c.id === colId ? { ...c, item_count: c.item_count + 1 } : c)
          );
        }}
        onClose={() => setSaveModal(null)} />

      }
    </AppLayout>);

}